// Verbindung zum eigenen Server. Das Sitzungs-Cookie schickt der Browser selbst mit.
import type { AccountTarget, ClosingAsset, ClosingGroup, ClosingView, ClosingYear } from "../shared/closing.js";
import type { InvExpense, InvTime, InvoiceFull, InvoiceInput, InvoiceMeta } from "../shared/invoice.js";
import type { DeliveryFull, DeliveryKind, DeliveryListItem, Platform, SocialBrand } from "../shared/delivery.js";
import type { ScriptContent, ScriptFull, ScriptListItem, ScriptStatus, ScriptType, ScriptVersionRow } from "../shared/script.js";
import type { AutoVideo, DriveScriptsResponse, ApiTokenRow, BilledRow, Me, NotificationRow, MilkeeOverview, MkAccount, MkCustomerFinance, MkDoc, MkEntry, MkInsights, MkProject, MkTime, MkTimer, State, StateKey, UserRow } from "../shared/types.js";
import type { VideoFeedback } from "../shared/sheet.js";
import type { CalendarResponse, EventDraft, MsCalendar, MsStatus } from "../shared/calendar.js";
import type { HealthStatus } from "../shared/health.js";

export interface GoogleInfo {
  configured: boolean;
  email: string;
  source: "upload" | "file" | null;
}

export interface NextcloudInfo {
  configured: boolean;
  url: string;
  user: string;
  root: string;
}

export interface MilkeeInfo {
  configured: boolean;
  company: string;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status = 0,
  ) {
    super(message);
  }
}

/** Rohdaten mit eigenem Content-Type (z. B. Upload-Stücke) */
export class RawBody {
  constructor(
    public blob: Blob,
    public type: string,
  ) {}
}

/** Meldet der Server eine Störung bei MILKEE, zeigt er den letzten Stand (Zeitpunkt in X-Milkee-Down) */
export const milkeeDown = { since: null as string | null, onChange: (_since: string | null) => {} };
function noteMilkee(r: Response) {
  if (!r.url.includes("/api/")) return;
  const v = r.headers.get("x-milkee-down");
  if (v === milkeeDown.since) return;
  milkeeDown.since = v;
  milkeeDown.onChange(v);
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  let r: Response;
  const form = body instanceof FormData;
  if (body instanceof RawBody) {
    try {
      r = await fetch(url, { method, credentials: "same-origin", headers: { "Content-Type": body.type }, body: body.blob });
    } catch {
      throw new ApiError("network", "Keine Verbindung zum Server");
    }
    noteMilkee(r);
    const j = (await r.json().catch(() => ({}))) as { code?: string; message?: string };
    if (!r.ok) throw new ApiError(j.code ?? `http_${r.status}`, j.message ?? r.statusText, r.status);
    return j as T;
  }
  try {
    r = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: body !== undefined && !form ? { "Content-Type": "application/json" } : {},
      ...(body !== undefined ? { body: form ? body : JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError("network", "Keine Verbindung zum Server");
  }
  noteMilkee(r);
  const j = (await r.json().catch(() => ({}))) as { code?: string; message?: string };
  if (!r.ok) throw new ApiError(j.code ?? `http_${r.status}`, j.message ?? r.statusText, r.status);
  return j as T;
}

export interface ScriptPatch {
  title?: string;
  status?: ScriptStatus;
  clientId?: string | null;
  projectId?: string | null;
  content?: ScriptContent;
  baseUpdatedAt?: string;
}

export interface DeliveryPatch {
  title?: string;
  kind?: DeliveryKind;
  message?: string;
  clientId?: string | null;
  projectId?: string | null;
  allowDownload?: boolean;
  showSafeZone?: boolean;
  active?: boolean;
  expiresAt?: string | null;
}

/** Push für die iPhone-App (APNs) */
export interface ApnsInfo {
  configured: boolean;
  keyId: string;
  teamId: string;
  bundleId: string;
  devices?: number;
}

export const api = {
  feedbackDone: (cid: string, commentId: string, done: boolean) => call<{ ok: true }>("PUT", `/api/live/${encodeURIComponent(cid)}/feedback`, { commentId, done }),
  liveFeedback: (cid: string, refresh = false) => call<{ feedback: Record<string, VideoFeedback>; email: string }>("GET", `/api/live/${encodeURIComponent(cid)}/feedback${refresh ? "?refresh=1" : ""}`),
  invoiceMeta: () => call<InvoiceMeta>("GET", "/api/milkee/invoice-meta"),
  invoice: (id: number) => call<{ invoice: InvoiceFull }>("GET", `/api/milkee/invoices/${id}`),
  createInvoice: (b: InvoiceInput) => call<{ invoice: InvoiceFull }>("POST", "/api/milkee/invoices", b),
  saveInvoice: (id: number, b: InvoiceInput) => call<{ invoice: InvoiceFull }>("PUT", `/api/milkee/invoices/${id}`, b),
  invoiceSources: (customerId: number) => call<{ times: InvTime[]; expenses: InvExpense[] }>("GET", `/api/milkee/invoice-sources?customerId=${customerId}`),
  deleteInvoice: (id: number) => call<{ ok: true }>("DELETE", `/api/milkee/invoices/${id}`),
  closing: (year: number, refresh = false) => call<{ closing: ClosingView }>("GET", `/api/closing/${year}${refresh ? "?refresh=1" : ""}`),
  saveClosing: (year: number, b: ClosingYear) => call<{ closing: ClosingView }>("PUT", `/api/closing/${year}`, b),
  saveClosingAssets: (assets: ClosingAsset[], year: number) => call<{ closing?: ClosingView }>("PUT", `/api/closing-assets?year=${year}`, { assets }),
  saveClosingEntries: (entries: Record<string, string | null>, year: number) => call<{ closing?: ClosingView }>("PUT", `/api/closing-entries?year=${year}`, { entries }),
  saveClosingGroups: (groups: ClosingGroup[], year: number) => call<{ closing?: ClosingView }>("PUT", `/api/closing-groups?year=${year}`, { groups }),
  saveClosingAccounts: (accounts: Record<string, AccountTarget | null>, year: number) => call<{ closing?: ClosingView }>("PUT", `/api/closing-accounts?year=${year}`, { accounts }),
  deliveries: (clientId?: string, archived = false) => {
    const q = new URLSearchParams({ ...(clientId ? { clientId } : {}), ...(archived ? { archived: "1" } : {}) }).toString();
    return call<{ deliveries: DeliveryListItem[]; archivedCount: number }>("GET", `/api/deliveries${q ? `?${q}` : ""}`);
  },
  reopenDelivery: (id: string) => call<{ delivery: DeliveryFull }>("POST", `/api/deliveries/${encodeURIComponent(id)}/reopen`, {}),
  dismissDeliveryRequest: (id: string) => call<{ delivery: DeliveryFull }>("DELETE", `/api/deliveries/${encodeURIComponent(id)}/request`),
  archiveDelivery: (id: string, archived: boolean) => call<{ delivery: DeliveryFull }>("POST", `/api/deliveries/${encodeURIComponent(id)}/archive`, { archived }),
  delivery: (id: string) => call<{ delivery: DeliveryFull }>("GET", `/api/deliveries/${encodeURIComponent(id)}`),
  createDelivery: (b: { title: string; message?: string; clientId?: string | null; projectId?: string | null; withCode?: boolean; kind?: DeliveryKind }) => call<{ delivery: DeliveryFull }>("POST", "/api/deliveries", b),
  saveDelivery: (id: string, b: DeliveryPatch) => call<{ delivery: DeliveryFull }>("PUT", `/api/deliveries/${encodeURIComponent(id)}`, b),
  deleteDelivery: (id: string) => call<{ ok: true }>("DELETE", `/api/deliveries/${encodeURIComponent(id)}`),
  deliveryCode: (id: string, b: { enabled?: boolean; code?: string; restore?: boolean }) => call<{ delivery: DeliveryFull }>("POST", `/api/deliveries/${encodeURIComponent(id)}/code`, b),
  deliveryToken: (id: string) => call<{ delivery: DeliveryFull }>("POST", `/api/deliveries/${encodeURIComponent(id)}/token`, {}),
  startUpload: (id: string, b: { title?: string; fileName: string; size: number; mime: string; replaceId?: string }) => call<{ video: { id: string; chunk: number } }>("POST", `/api/deliveries/${encodeURIComponent(id)}/videos`, b),
  completeUpload: (id: string, vid: string, b: { duration: number; width: number; height: number }) => call<{ delivery: DeliveryFull }>("POST", `/api/deliveries/${encodeURIComponent(id)}/videos/${encodeURIComponent(vid)}/complete`, b),
  /** Rohdaten (Upload-Stück oder JPEG) senden */
  putRaw: (url: string, body: Blob, type: string) => call<{ received?: number; ok?: true }>("PUT", url, new RawBody(body, type)),
  socialSettings: () => call<SocialBrand>("GET", "/api/settings/social"),
  saveSocialSettings: (b: SocialBrand) => call<SocialBrand>("PUT", "/api/settings/social", b),
  saveDeliveryVideo: (id: string, vid: string, b: { title?: string; platforms?: Platform[]; caption?: string; platformCaption?: { platform: Platform; text: string } }) => call<{ ok: true }>("PUT", `/api/deliveries/${encodeURIComponent(id)}/videos/${encodeURIComponent(vid)}`, b),
  deleteDeliveryVideo: (id: string, vid: string) => call<{ ok: true }>("DELETE", `/api/deliveries/${encodeURIComponent(id)}/videos/${encodeURIComponent(vid)}`),
  orderDeliveryVideos: (id: string, ids: string[]) => call<{ ok: true }>("POST", `/api/deliveries/${encodeURIComponent(id)}/videos/order`, { ids }),
  doneDeliveryComment: (id: string, cid: string, done: boolean) => call<{ ok: true }>("PUT", `/api/deliveries/${encodeURIComponent(id)}/comments/${encodeURIComponent(cid)}`, { done }),
  deleteDeliveryComment: (id: string, cid: string) => call<{ ok: true }>("DELETE", `/api/deliveries/${encodeURIComponent(id)}/comments/${encodeURIComponent(cid)}`),

  scripts: (clientId?: string) => call<{ scripts: ScriptListItem[] }>("GET", `/api/scripts${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ""}`),
  script: (id: string) => call<{ script: ScriptFull }>("GET", `/api/scripts/${encodeURIComponent(id)}`),
  createScript: (b: { title: string; type: ScriptType; clientId?: string | null; projectId?: string | null; content?: unknown }) => call<{ script: ScriptFull }>("POST", "/api/scripts", b),
  saveScript: (id: string, b: ScriptPatch) => call<{ script: ScriptFull }>("PUT", `/api/scripts/${encodeURIComponent(id)}`, b),
  deleteScript: (id: string) => call<{ ok: true }>("DELETE", `/api/scripts/${encodeURIComponent(id)}`),
  duplicateScript: (id: string) => call<{ script: ScriptFull }>("POST", `/api/scripts/${encodeURIComponent(id)}/duplicate`, {}),
  scriptVersions: (id: string) => call<{ versions: ScriptVersionRow[] }>("GET", `/api/scripts/${encodeURIComponent(id)}/versions`),
  freezeScript: (id: string, note: string) => call<{ script: ScriptFull }>("POST", `/api/scripts/${encodeURIComponent(id)}/versions`, { note }),
  restoreScript: (id: string, vid: string) => call<{ script: ScriptFull }>("POST", `/api/scripts/${encodeURIComponent(id)}/versions/${encodeURIComponent(vid)}/restore`, {}),

  me: () => call<Me>("GET", "/api/auth/me"),
  login: (email: string, password: string) => call<Me>("POST", "/api/auth/login", { email, password }),
  logout: () => call<{ ok: true }>("POST", "/api/auth/logout", {}),
  mfa: (code: string) => call<Me>("POST", "/api/auth/mfa", { code }),
  totpSetup: () => call<{ qr: string; secret: string; uri: string }>("POST", "/api/auth/totp/setup", {}),
  totpEnable: (code: string) => call<Me>("POST", "/api/auth/totp/enable", { code }),
  password: (next: string, current?: string) => call<Me>("POST", "/api/auth/password", { next, ...(current !== undefined ? { current } : {}) }),

  state: () => call<{ state: State; rev: number }>("GET", "/api/state"),
  rev: () => call<{ rev: number }>("GET", "/api/rev"),
  put: (key: StateKey, value: unknown) => call<{ rev: number }>("PUT", `/api/state/${key}`, { value }),

  sheetsInfo: () => call<{ enabled: boolean; email: string }>("GET", "/api/sheets/info"),
  sheets: <T = unknown>(body: unknown) => call<T>("POST", "/api/sheets", body),

  users: () => call<{ users: UserRow[] }>("GET", "/api/users"),
  createUser: (email: string, name: string, role: string) => call<{ user: UserRow; password: string }>("POST", "/api/users", { email, name, role }),
  updateUser: (id: string, data: { name?: string; role?: string; disabled?: boolean }) => call<{ user: UserRow }>("PATCH", `/api/users/${id}`, data),
  resetPassword: (id: string) => call<{ password: string }>("POST", `/api/users/${id}/reset-password`, {}),
  reset2fa: (id: string) => call<{ ok: true }>("POST", `/api/users/${id}/reset-2fa`, {}),
  deleteUser: (id: string) => call<{ ok: true }>("DELETE", `/api/users/${id}`),

  google: () => call<GoogleInfo>("GET", "/api/settings/google"),
  uploadGoogle: (key: string) => call<GoogleInfo>("PUT", "/api/settings/google", { key }),
  removeGoogle: () => call<GoogleInfo>("DELETE", "/api/settings/google"),

  calendar: (from: string, to: string) => call<CalendarResponse>("GET", `/api/calendar?from=${from}&to=${to}`),
  eventCreate: (d: EventDraft) => call<{ id: string }>("POST", "/api/calendar/events", d),
  eventUpdate: (id: string, d: EventDraft) => call<{ ok: true }>("PATCH", `/api/calendar/events/${encodeURIComponent(id)}`, d),
  eventDelete: (id: string) => call<{ ok: true }>("DELETE", `/api/calendar/events/${encodeURIComponent(id)}`),
  healthStatus: () => call<HealthStatus>("GET", "/api/health/status"),
  healthCheck: () => call<HealthStatus>("POST", "/api/health/check", {}),
  calendarSync: () => call<MsStatus>("POST", "/api/calendar/sync", {}),
  msSettings: () => call<MsStatus>("GET", "/api/settings/microsoft"),
  msSave: (b: { tenantId: string; clientId: string; clientSecret?: string }) => call<MsStatus>("PUT", "/api/settings/microsoft", b),
  msRemove: () => call<MsStatus>("DELETE", "/api/settings/microsoft"),
  msCalendars: () => call<{ calendars: MsCalendar[] }>("GET", "/api/ms/calendars"),
  msSetCalendar: (b: { id: string; show: string[] }) => call<MsStatus>("PUT", "/api/settings/microsoft/calendar", b),
  nextcloudSettings: () => call<NextcloudInfo>("GET", "/api/settings/nextcloud"),
  connectNextcloud: (b: { url: string; user: string; pass?: string; root: string }) => call<NextcloudInfo>("PUT", "/api/settings/nextcloud", b),
  disconnectNextcloud: () => call<NextcloudInfo>("DELETE", "/api/settings/nextcloud"),
  apnsSettings: () => call<ApnsInfo>("GET", "/api/settings/apns"),
  saveApns: (b: { keyId: string; teamId: string; key: string }) => call<ApnsInfo>("PUT", "/api/settings/apns", b),
  removeApns: () => call<ApnsInfo>("DELETE", "/api/settings/apns"),
  pushTest: () => call<{ sent: number; failed: string[] }>("POST", "/api/push/test", {}),
  retryDeliveryVideo: (id: string, vid: string) => call<{ delivery: DeliveryFull }>("POST", `/api/deliveries/${encodeURIComponent(id)}/videos/${encodeURIComponent(vid)}/retry`, {}),
  milkeeSettings: () => call<MilkeeInfo>("GET", "/api/settings/milkee"),
  connectMilkee: (token: string) => call<MilkeeInfo>("PUT", "/api/settings/milkee", { token }),
  disconnectMilkee: () => call<MilkeeInfo>("DELETE", "/api/settings/milkee"),
  milkeeStatus: () => call<MilkeeInfo>("GET", "/api/milkee/status"),
  milkeeOverview: (refresh = false) => call<MilkeeOverview>("GET", `/api/milkee/overview${refresh ? "?refresh=1" : ""}`),
  milkeeCustomers: () => call<{ customers: { id: number; name: string }[] }>("GET", "/api/milkee/customers"),
  billed: (clientId: string) => call<{ billed: BilledRow[] }>("GET", `/api/milkee/billed?clientId=${encodeURIComponent(clientId)}`),
  bill: (clientId: string, items: { key: string; title: string; section: string }[]) =>
    call<{ billed: BilledRow[]; skipped: number; invoice: { id: number; number: string; total: number } | null; created: boolean }>("POST", "/api/milkee/bill", { clientId, items }),
  skipBilled: (clientId: string, items: { key: string; title: string }[]) => call<{ marked: number }>("POST", "/api/milkee/billed/skip", { clientId, items }),
  driveScripts: (clientId: string, refresh = false) => call<DriveScriptsResponse>("GET", `/api/drive/scripts?clientId=${encodeURIComponent(clientId)}${refresh ? "&refresh=1" : ""}`),
  chooseDriveScript: (clientId: string, itemKey: string, fileId: string | null) => call<{ ok: true }>("POST", "/api/drive/scripts", { clientId, itemKey, fileId }),
  automation: (clientId: string) => call<{ items: AutoVideo[] }>("GET", `/api/milkee/automation?clientId=${encodeURIComponent(clientId)}`),
  mkSync: (clientId: string) => call<{ results: { created: string[]; closed: string[]; billed: string[]; errors: string[] }[] }>("POST", `/api/milkee/sync?clientId=${encodeURIComponent(clientId)}`, {}),
  billEarly: (clientId: string, keys: string[]) => call<{ closed: string[]; errors: string[] }>("POST", "/api/milkee/bill-early", { clientId, keys }),
  billEarlyUndo: (clientId: string, key: string) => call<{ ok: true }>("POST", "/api/milkee/bill-early/undo", { clientId, key }),
  unbill: (id: string) => call<{ ok: true }>("DELETE", `/api/milkee/billed/${id}`),

  mkProjects: () => call<{ projects: MkProject[] }>("GET", "/api/milkee/projects"),
  mkCreateProject: (name: string, customerId: number) => call<{ project: MkProject }>("POST", "/api/milkee/projects", { name, customerId }),
  mkUpdateProject: (id: number, data: { name?: string; kanbanStatus?: string | null; endDate?: string | null; archived?: boolean }) => call<{ project: MkProject }>("PUT", `/api/milkee/projects/${id}`, data),
  mkTimes: (from: string, to: string, projectId?: number) => call<{ times: MkTime[] }>("GET", `/api/milkee/times?from=${from}&to=${to}${projectId ? `&projectId=${projectId}` : ""}`),
  mkAddTime: (t: { projectId: number; date: string; hours: number; minutes: number; description: string; billable: boolean; start?: string; end?: string }) => call<{ time: MkTime }>("POST", "/api/milkee/times", t),
  mkDeleteTime: (id: number) => call<{ ok: true }>("DELETE", `/api/milkee/times/${id}`),
  mkTimer: () => call<MkTimer>("GET", "/api/milkee/timer"),
  mkStartTimer: (projectId: number) => call<MkTimer>("POST", "/api/milkee/timer/start", { projectId }),
  mkStopTimer: (description: string) => call<MkTimer>("POST", "/api/milkee/timer/stop", { description }),
  mkInsights: (from: string, to: string, refresh = false) => call<MkInsights>("GET", `/api/milkee/insights?from=${from}&to=${to}${refresh ? "&refresh=1" : ""}`),
  mkCustomerFinance: (id: number, from: string, to: string, refresh = false) => call<MkCustomerFinance>("GET", `/api/milkee/customers/${id}/finance?from=${from}&to=${to}${refresh ? "&refresh=1" : ""}`),
  mkDocs: (kind: "invoice" | "proposal", status: string, year?: number) => call<{ documents: MkDoc[] }>("GET", `/api/milkee/documents?kind=${kind}&status=${status}${year ? `&year=${year}` : ""}`),
  mkMark: (kind: "invoice" | "proposal", id: number, status: string, paidDate?: string) => call<{ ok: true }>("POST", `/api/milkee/documents/${kind}/${id}/mark`, { status, ...(paidDate ? { paidDate } : {}) }),
  mkAccounts: () => call<{ accounts: MkAccount[] }>("GET", "/api/milkee/accounts"),
  mkEntries: (year: number, type: string) => call<{ entries: MkEntry[] }>("GET", `/api/milkee/entries?year=${year}${type ? `&type=${type}` : ""}`),
  mkAddEntry: (form: FormData) => call<{ entry: MkEntry }>("POST", "/api/milkee/entries", form),

  notifications: () => call<{ notifications: NotificationRow[] }>("GET", "/api/notifications"),
  dismiss: (id: string) => call<{ ok: true }>("DELETE", `/api/notifications/${id}`),
  dismissAll: () => call<{ ok: true }>("DELETE", "/api/notifications"),
  notificationsSeen: () => call<{ ok: true }>("POST", "/api/notifications/seen", {}),

  tokens: () => call<{ tokens: ApiTokenRow[] }>("GET", "/api/tokens"),
  createToken: (name: string, scope: string, days: number) => call<{ token: ApiTokenRow; secret: string }>("POST", "/api/tokens", { name, scope, days }),
  deleteToken: (id: string) => call<{ ok: true }>("DELETE", `/api/tokens/${id}`),
};
